import { Maatregel, RijdtMaatregel, VertrekTijd } from "@/entities/vsm";
import { Form, FormFieldInput } from "@/shared/components";
import { uuid } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormButtons, OpmerkingField, RichtingSwitch, SerieInput, StationSelect } from "./components.tsx";

const rijdtMaatregelSchema = z.discriminatedUnion("richting", [
    // ViceVersa variant - Van en Naar stations required
    z.object({
        type: z.literal("Rijdt"),
        treinserie: z.string().min(1, "Serie is verplicht"),
        richting: z.literal("ViceVersa"),
        opmerking: z.string().optional(),
    }),
    // Heen variant - Van en Naar stations required + optional vertrekTijd
    z.object({
        type: z.literal("Rijdt"),
        treinserie: z.string().min(1, "Serie is verplicht"),
        richting: z.literal("Heen"),
        vanStationCode: z
            .string({ error: (issue) => (issue.input === undefined ? "Verplicht" : undefined) })
            .min(1, "Van station is verplicht"),
        naarStationCode: z
            .string({ error: (issue) => (issue.input === undefined ? "Verplicht" : undefined) })
            .min(1, "Naar station is verplicht"),
        vertrekTijd: VertrekTijd.zodType,
        opmerking: z.string().optional(),
    }),
]);

export type RijdtMaatregelFormData = z.infer<typeof rijdtMaatregelSchema>;

interface RijdtMaatregelFormProps {
    type: "create" | "update";
    onSubmit: (data: Maatregel) => void;
    onCancel: () => void;
    initialData?: RijdtMaatregel | undefined;
}

export function RijdtMaatregelForm({ onSubmit, onCancel, initialData, type }: Readonly<RijdtMaatregelFormProps>) {
    const form = useForm<RijdtMaatregelFormData>({
        resolver: zodResolver(rijdtMaatregelSchema),
        defaultValues: {
            type: "Rijdt",
            richting: initialData?.richting ?? "ViceVersa",
            treinserie: initialData?.treinserie ?? "",
            opmerking: initialData?.opmerking ?? "",
            vanStationCode: initialData?.vanStationCode ?? undefined,
            naarStationCode: initialData?.naarStationCode ?? undefined,
            vertrekTijd: VertrekTijd.toMinutes(initialData?.vertrekTijd),
        },
    });
    const { handleSubmit, watch, formState } = form;
    const isDirty = form.formState.isDirty;

    const richting = watch("richting");
    const treinserie = watch("treinserie");
    const isHeenSelected = richting === "Heen";

    const onSubmitForm = (data: RijdtMaatregelFormData) => {
        if (!isDirty) {
            onCancel();
            return;
        }
        let maatregel = convertToMaatregel(data);
        if (type === "update" && initialData) {
            maatregel = Maatregel.preserveMetadataForUpdate(maatregel, initialData.id, initialData.handmatigToegevoegd);
        }
        onSubmit(maatregel);
    };

    return (
        <div className="bg-emphasis-primary rounded-lg p-4">
            <Form {...form}>
                <form onSubmit={(e) => void handleSubmit(onSubmitForm)(e)} className="space-y-2">
                    <div className="flex flex-row gap-4">
                        <SerieInput form={form} name="treinserie" />
                        <RichtingSwitch form={form} name="richting" />
                    </div>

                    {isHeenSelected && (
                        <div className="flex w-full min-w-0 items-center gap-4">
                            <FormFieldInput form={form} name="vertrekTijd" placeholder=":min" className="max-w-[80px]" />
                            <StationSelect form={form} name="vanStationCode" placeholder="Van" forTreinserie={treinserie} />
                            <StationSelect form={form} name="naarStationCode" placeholder="Naar" forTreinserie={treinserie} />
                        </div>
                    )}

                    <OpmerkingField form={form} name="opmerking" />
                    <FormButtons
                        onCancel={onCancel}
                        isSubmitting={formState.isSubmitting}
                        submitLabel={type === "create" ? "Toevoegen" : "Opslaan"}
                    />
                </form>
            </Form>
        </div>
    );
}

function convertToMaatregel(data: RijdtMaatregelFormData): Maatregel {
    return {
        id: uuid(),
        type: data.type,
        treinserie: data.treinserie,
        richting: data.richting,
        vanStationCode: data.richting === "Heen" ? data.vanStationCode : undefined,
        naarStationCode: data.richting === "Heen" ? data.naarStationCode : undefined,
        vertrekTijd: data.richting === "Heen" && data.vertrekTijd ? VertrekTijd.toVertrekTijd(data.vertrekTijd) : undefined,
        opmerking: data.opmerking,
        handmatigToegevoegd: true,
        status: "Nieuw",
        opmerkingIsSpoorwebSuggestie: false,
    };
}
